import api from './api';

/**
 * Service pour gérer les préférences du tableau de bord
 */
const dashboardService = {
    /**
     * Récupérer les préférences du tableau de bord de l'utilisateur
     * @returns {Promise<Object>} Préférences du tableau de bord
     */
    getUserPreferences: async () => {
        try {
            console.log('Appel API: GET /dashboard/preferences');
            // Ne pas inclure /api/v1 car il est déjà dans baseURL
            const response = await api.get('/dashboard/preferences');
            console.log('Réponse API:', response.data);
            return response.data.preferences;
        } catch (error) {
            console.error('Erreur lors de la récupération des préférences du tableau de bord:', error);
            // En cas d'erreur, retourner les préférences par défaut
            return getDefaultPreferences();
        }
    },

    /**
     * Récupérer les préférences du tableau de bord d'administration
     * @returns {Promise<Object>} Préférences du tableau de bord d'administration
     */
    getAdminPreferences: async () => {
        try {
            console.log('Appel API: GET /dashboard/admin-preferences');
            const response = await api.get('/dashboard/admin-preferences');
            return response.data.preferences;
        } catch (error) {
            console.error('Erreur lors de la récupération des préférences du tableau de bord d\'administration:', error);
            // En cas d'erreur, retourner les préférences par défaut
            return getDefaultPreferences();
        }
    },

    /**
     * Sauvegarder les préférences du tableau de bord de l'utilisateur
     * @param {Object} preferences - Préférences à sauvegarder
     * @returns {Promise<Object>} Préférences sauvegardées
     */
    saveUserPreferences: async (preferences) => {
        try {
            console.log('Appel API: POST /dashboard/preferences', preferences);
            const response = await api.post('/dashboard/preferences', preferences);
            return response.data.preferences;
        } catch (error) {
            console.error('Erreur lors de la sauvegarde des préférences du tableau de bord:', error);
            throw error;
        }
    },

    /**
     * Réinitialiser les préférences du tableau de bord aux valeurs par défaut
     * @returns {Promise<Object>} Préférences réinitialisées
     */
    resetToDefault: async () => {
        try {
            console.log('Appel API: POST /dashboard/preferences/reset');
            const response = await api.post('/dashboard/preferences/reset');
            return response.data.preferences;
        } catch (error) {
            console.error('Erreur lors de la réinitialisation des préférences du tableau de bord:', error);
            // En cas d'erreur, retourner les préférences par défaut
            return getDefaultPreferences();
        }
    },

    /**
     * Mettre à jour la disposition des widgets
     * @param {Array} widgetsLayout - Nouvelle disposition des widgets
     * @returns {Promise<Object>} Préférences mises à jour
     */
    updateWidgetsLayout: async (widgetsLayout) => {
        try {
            console.log('Appel API: PATCH /dashboard/preferences/widgets', { widgets: widgetsLayout });
            const response = await api.patch('/dashboard/preferences/widgets', { widgets: widgetsLayout });
            return response.data.preferences;
        } catch (error) {
            console.error('Erreur lors de la mise à jour de la disposition des widgets:', error);
            throw error;
        }
    },

    /**
     * Mettre à jour le thème du tableau de bord
     * @param {string} theme - Nouveau thème (light, dark, custom)
     * @param {Object} colors - Couleurs personnalisées (pour le thème custom)
     * @returns {Promise<Object>} Préférences mises à jour
     */
    updateTheme: async (theme, colors = null) => {
        try {
            const payload = { theme };
            if (colors) {
                payload.colors = colors;
            }
            console.log('Appel API: PATCH /dashboard/preferences/theme', payload);
            const response = await api.patch('/dashboard/preferences/theme', payload);
            return response.data.preferences;
        } catch (error) {
            console.error('Erreur lors de la mise à jour du thème:', error);
            throw error;
        }
    },

    /**
     * Fonction utilitaire pour obtenir les préférences par défaut
     * Exposée pour être utilisée par le contexte DashboardContext
     */
    getDefaultPreferences
};

/**
 * Obtenir les préférences par défaut
 * @returns {Object} Préférences par défaut
 */
function getDefaultPreferences() {
    return {
        layout: 'grid',
        theme: 'light',
        colors: {
            primary: '#3f51b5',
            secondary: '#f50057',
            success: '#4caf50',
            warning: '#ff9800',
            error: '#f44336',
            info: '#2196f3'
        },
        widgets: [
            {
                id: 'users-stats',
                type: 'card',
                title: 'Utilisateurs',
                enabled: true,
                position: 0,
                size: 'small',
                icon: 'PeopleOutline',
                color: '#3f51b5',
                link: '/admin/users'
            },
            {
                id: 'categories-stats',
                type: 'card',
                title: 'Catégories',
                enabled: true,
                position: 1,
                size: 'small',
                icon: 'CategoryOutlined',
                color: '#f50057',
                link: '/admin/categories'
            },
            {
                id: 'properties-stats',
                type: 'card',
                title: 'Propriétés',
                enabled: true,
                position: 2,
                size: 'small',
                icon: 'HomeOutlined',
                color: '#4caf50',
                link: '/admin/properties'
            },
            {
                id: 'roles-stats',
                type: 'card',
                title: 'Rôles',
                enabled: true,
                position: 3,
                size: 'small',
                icon: 'SecurityOutlined',
                color: '#ff9800',
                link: '/admin/roles'
            },
            {
                id: 'permissions-stats',
                type: 'card',
                title: 'Permissions',
                enabled: true,
                position: 4,
                size: 'small',
                icon: 'BookmarkOutlined',
                color: '#f44336',
                link: '/admin/permissions'
            },
            {
                id: 'verifications-stats',
                type: 'card',
                title: 'Vérifications',
                enabled: true,
                position: 5,
                size: 'small',
                icon: 'ErrorOutline',
                color: '#2196f3',
                link: '/admin/verifications'
            },
            {
                id: 'users-distribution',
                type: 'chart',
                chartType: 'pie',
                title: 'Répartition des utilisateurs',
                enabled: true,
                position: 6,
                size: 'medium',
                link: '/admin/users'
            },
            {
                id: 'activity-timeline',
                type: 'chart',
                chartType: 'area',
                title: 'Activité au fil du temps',
                enabled: true,
                position: 7,
                size: 'medium',
                link: '/admin/statistics'
            },
            {
                id: 'recent-users',
                type: 'list',
                title: 'Utilisateurs récents',
                enabled: true,
                position: 8,
                size: 'medium',
                maxItems: 5,
                link: '/admin/users'
            },
            {
                id: 'system-roles',
                type: 'list',
                title: 'Rôles du système',
                enabled: true,
                position: 9,
                size: 'medium',
                maxItems: 5,
                link: '/admin/roles'
            }
        ]
    };
}

export default dashboardService;